import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import {
  Building2, Download, MapPin, DollarSign, TrendingUp, User, Mail,
  Phone, ArrowRight, Check, Eye, Percent,
} from "lucide-react";

const fmtMoney = (n) => (n == null || Number.isNaN(+n))
  ? "—"
  : `$${Math.round(+n).toLocaleString()}`;
const fmtPct = (n) => (n == null || Number.isNaN(+n)) ? "—" : `${(+n).toFixed(2)}%`;

/**
 * Public branded landing page for a shared Loan Quote.
 * URL: /q/:slug  (optional ?a=<agent-name> for per-agent tracking)
 *
 * Purpose: listing agents embed this URL on their MLS/website listings. Anyone
 * who clicks it sees a professional Byrd & CO branded page with property,
 * loan-option summaries, the listing agent card, a Download PDF button, a
 * Request Info form, and a live visit counter.
 */
export default function PublicQuoteLanding() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const referringAgent = (params.get("a") || "").trim();

  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    api.get(`/public/loan-quote/${slug}`, { params: referringAgent ? { agent: referringAgent } : {} })
      .then((r) => setData(r.data))
      .catch(() => setNotFound(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const pdfHref = useMemo(() => data ? `${API_BASE}/public/loan-quote/${slug}/pdf` : "#", [data, slug]);
  const agentPhotoHref = useMemo(
    () => (data?.agent_photo_url ? `${API_BASE}${data.agent_photo_url.replace(/^\/api/, "")}` : null),
    [data]
  );

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submitLead = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please share your name and email so we can follow up.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/public/loan-quote/${slug}/lead`, {
        ...form,
        agent: referringAgent,
      });
      setSubmitted(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send request — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#FBF8F1] grid place-items-center px-4">
        <div className="byrd-card p-10 max-w-lg text-center" data-testid="pq-not-found">
          <h1 className="font-serif text-3xl font-bold">Quote link not available.</h1>
          <p className="text-[#6B6558] mt-3">
            This link may have been disabled by the broker. Reach out directly at{" "}
            <a href="mailto:wayne@byrd-co.com" className="underline">wayne@byrd-co.com</a>.
          </p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#FBF8F1] grid place-items-center">
        <div className="text-[#6B6558] font-mono text-xs uppercase tracking-widest">Loading quote…</div>
      </div>
    );
  }

  const p = data.property_info || {};
  const options = data.options || [];
  const agent = data.listing_agent || {};
  const hasAgent = agent.name || agent.email || agent.brokerage;

  return (
    <div className="min-h-screen bg-[#FBF8F1] flex flex-col" data-testid="pq-landing">
      {/* Header */}
      <header className="border-b border-[#E4DFD1] bg-white">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Building2 size={22} className="text-[#C89434]" />
            <div>
              <div className="font-serif text-lg font-bold">Byrd &amp; CO</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#6B6558]">
                Commercial Real Estate Lending
              </div>
            </div>
          </div>
          <div className="text-right hidden sm:block">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#6B6558]">// Financing Snapshot</div>
            <div className="text-xs text-[#6B6558] flex items-center gap-1 justify-end mt-0.5">
              <Eye size={12} /> <span data-testid="pq-view-count">{data.total_views}</span> {data.total_views === 1 ? "view" : "views"}
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-5xl w-full mx-auto px-6 py-8 md:py-12">
        {/* Hero: property */}
        <section className="byrd-card p-6 md:p-8 mb-6" data-testid="pq-hero">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#C89434]">// Loan Quote</div>
          <h1 className="font-serif text-3xl md:text-5xl font-bold mt-2 leading-tight">
            {p.name || p.address || "Property Financing Snapshot"}
          </h1>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-4 text-sm text-[#6B6558]">
            {(p.address || p.city || p.state) && (
              <div className="flex items-center gap-1.5">
                <MapPin size={14} />
                <span>{[p.address, p.city, p.state].filter(Boolean).join(", ")}</span>
              </div>
            )}
            {p.property_type && (
              <div className="flex items-center gap-1.5">
                <Building2 size={14} /> <span>{p.property_type}</span>
              </div>
            )}
            {p.estimated_value != null && (
              <div className="flex items-center gap-1.5">
                <DollarSign size={14} /> <span>Value {fmtMoney(p.estimated_value)}</span>
              </div>
            )}
            {p.noi != null && (
              <div className="flex items-center gap-1.5">
                <TrendingUp size={14} /> <span>NOI {fmtMoney(p.noi)}</span>
              </div>
            )}
            {p.cap_rate_pct != null && (
              <div className="flex items-center gap-1.5">
                <Percent size={14} /> <span>Cap {fmtPct(p.cap_rate_pct)}</span>
              </div>
            )}
          </div>
          <div className="mt-6">
            <a
              href={pdfHref}
              target="_blank"
              rel="noopener noreferrer"
              className="byrd-btn byrd-btn-dark"
              data-testid="pq-download-pdf"
            >
              <Download size={14} /> Download Full Quote (PDF)
            </a>
          </div>
        </section>

        {/* Loan option table */}
        {options.length > 0 && (
          <section className="byrd-card p-6 md:p-8 mb-6" data-testid="pq-options">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#6B6558]">// Indicative Financing Options</div>
            <h2 className="font-serif text-2xl font-bold mt-1 mb-4">
              {options.length === 1 ? "Financing Snapshot" : `${options.length} Financing Options`}
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b border-[#E4DFD1] text-[10px] uppercase tracking-widest text-[#6B6558]">
                    <th className="p-2">Structure</th>
                    <th className="p-2 text-right">Loan Amount</th>
                    <th className="p-2 text-right">LTV</th>
                    <th className="p-2 text-right">Rate</th>
                    <th className="p-2 text-right">Monthly P&amp;I</th>
                    <th className="p-2">Recourse</th>
                  </tr>
                </thead>
                <tbody>
                  {options.map((o, i) => (
                    <tr key={i} className="border-b border-[#F3EEE0]" data-testid={`pq-option-${i}`}>
                      <td className="p-2">
                        <div className="font-semibold">{o.label || "Bank"}</div>
                        {o.loan_program && (
                          <div className="text-[11px] text-[#6B6558]">{o.loan_program}</div>
                        )}
                      </td>
                      <td className="p-2 text-right font-mono">{fmtMoney(o.loan_amount)}</td>
                      <td className="p-2 text-right font-mono">{fmtPct(o.ltv_pct)}</td>
                      <td className="p-2 text-right font-mono">{fmtPct(o.interest_rate_pct)}</td>
                      <td className="p-2 text-right font-mono">{fmtMoney(o.monthly_payment)}</td>
                      <td className="p-2 text-xs">{o.recourse || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-[11px] text-[#6B6558] mt-4">
              Indicative terms only. Final structure subject to lender underwriting, market conditions, and property performance.
            </p>
          </section>
        )}

        <div className="grid grid-cols-1 md:grid-cols-[1fr_360px] gap-6">
          {/* Request info form */}
          <section className="byrd-card p-6 md:p-8" data-testid="pq-lead-form-card">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#C89434]">// Request Info</div>
            <h2 className="font-serif text-2xl font-bold mt-1 mb-1">Interested in this property?</h2>
            <p className="text-sm text-[#6B6558] mb-4">
              Tell us a bit about you and we'll follow up within one business day with a live-market quote or a walkthrough of these numbers.
            </p>
            {submitted ? (
              <div className="rounded-md border border-[#C89434]/30 bg-[#F3EEE0] p-5 flex gap-3 items-start" data-testid="pq-lead-thanks">
                <div className="w-9 h-9 rounded-full bg-[#C89434] text-white grid place-items-center shrink-0">
                  <Check size={16} />
                </div>
                <div>
                  <div className="font-serif text-lg font-bold">Thanks — we've got it.</div>
                  <p className="text-sm text-[#6B6558] mt-1">
                    Wayne or a Byrd &amp; CO teammate will reach out shortly. Feel free to download the full PDF above in the meantime.
                  </p>
                </div>
              </div>
            ) : (
              <form onSubmit={submitLead} className="space-y-3" data-testid="pq-lead-form">
                <Field label="Your name" required>
                  <input
                    value={form.name}
                    onChange={set("name")}
                    className="w-full h-11 px-3 rounded-md border border-[#E4DFD1] bg-white text-sm"
                    data-testid="pq-lead-name"
                    required
                  />
                </Field>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Email" required>
                    <input
                      type="email"
                      value={form.email}
                      onChange={set("email")}
                      className="w-full h-11 px-3 rounded-md border border-[#E4DFD1] bg-white text-sm"
                      data-testid="pq-lead-email"
                      required
                    />
                  </Field>
                  <Field label="Phone">
                    <input
                      value={form.phone}
                      onChange={set("phone")}
                      className="w-full h-11 px-3 rounded-md border border-[#E4DFD1] bg-white text-sm"
                      data-testid="pq-lead-phone"
                    />
                  </Field>
                </div>
                <Field label="Anything specific we can address?">
                  <textarea
                    value={form.message}
                    onChange={set("message")}
                    rows={4}
                    className="w-full px-3 py-2 rounded-md border border-[#E4DFD1] bg-white text-sm"
                    data-testid="pq-lead-message"
                    placeholder="e.g., I'm curious about seller-carry or 25% down…"
                  />
                </Field>
                <button
                  type="submit"
                  disabled={submitting}
                  className="byrd-btn byrd-btn-dark w-full sm:w-auto"
                  data-testid="pq-lead-submit"
                >
                  {submitting ? "Sending…" : (<>Request Info <ArrowRight size={14} /></>)}
                </button>
              </form>
            )}
          </section>

          {/* Sidebar: listing agent + trust */}
          <aside className="space-y-4">
            {hasAgent && (
              <div className="byrd-card p-5" data-testid="pq-agent-card">
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#6B6558]">// Listing Agent</div>
                <div className="mt-3 flex items-start gap-3">
                  {agentPhotoHref ? (
                    <img
                      src={agentPhotoHref}
                      alt={agent.name || "Listing agent"}
                      className="w-14 h-14 rounded-full object-cover border border-[#E4DFD1]"
                    />
                  ) : (
                    <div className="w-14 h-14 rounded-full bg-[#F3EEE0] text-[#C89434] grid place-items-center border border-[#E4DFD1]">
                      <User size={20} />
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="font-serif text-lg font-bold leading-tight">{agent.name || "—"}</div>
                    {agent.brokerage && <div className="text-xs text-[#6B6558]">{agent.brokerage}</div>}
                  </div>
                </div>
                <div className="mt-3 space-y-1 text-sm">
                  {agent.email && (
                    <a href={`mailto:${agent.email}`} className="flex items-center gap-1.5 text-[#23446E] hover:underline">
                      <Mail size={13} /> {agent.email}
                    </a>
                  )}
                  {agent.phone && (
                    <a href={`tel:${agent.phone}`} className="flex items-center gap-1.5 text-[#23446E] hover:underline">
                      <Phone size={13} /> {agent.phone}
                    </a>
                  )}
                </div>
              </div>
            )}
            <div className="byrd-card p-5" data-testid="pq-broker-card">
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#6B6558]">// Financing By</div>
              <div className="mt-3">
                <div className="font-serif text-lg font-bold leading-tight">Byrd &amp; CO</div>
                <div className="text-xs text-[#6B6558]">Commercial Real Estate Lending</div>
              </div>
              <div className="mt-3 space-y-1 text-sm">
                <a href="mailto:wayne@byrd-co.com" className="flex items-center gap-1.5 text-[#23446E] hover:underline">
                  <Mail size={13} /> wayne@byrd-co.com
                </a>
                <a href="tel:8328139802" className="flex items-center gap-1.5 text-[#23446E] hover:underline">
                  <Phone size={13} /> (832) 813-9802
                </a>
              </div>
            </div>
          </aside>
        </div>
      </main>

      <footer className="border-t border-[#E4DFD1] bg-white">
        <div className="max-w-5xl mx-auto px-6 py-6 text-xs text-[#6B6558] flex flex-wrap items-center justify-between gap-2">
          <div>© Byrd &amp; CO — All rights reserved.</div>
          <div className="font-mono uppercase tracking-widest text-[10px]">Loan Quote · Shared Link</div>
        </div>
      </footer>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <div className="text-[10px] font-mono uppercase tracking-widest text-[#6B6558] mb-1">
        {label}{required && <span className="text-[#C89434]"> *</span>}
      </div>
      {children}
    </label>
  );
}
